// ETSY ONIZLEMESI — taslagin alicinin Etsy urun sayfasinda nasil gorunecegi:
// fotograf galerisi, baslik, fiyat (indirimli + ustu cizili liste fiyati),
// varyasyon listeleri (secilen secenegin fiyati), kisisellestirme sorulari.
// Katalog, Liste Denetimi ve Listing Builder ayni bileseni kullanir.

import { useMemo, useState } from "react";
import { LIST_PRICE_FACTOR } from "../../lib/jewelryListing";
import { getBackendUrl } from "../../lib/http";

/** Goreli adresler ("/api/files/...") API adresiyle tamamlanir */
const abs = (u?: string) => (!u ? "" : /^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u}`);

interface PreviewOption { label: string; sellingPrice?: number; included?: boolean }
interface PreviewVariation { name: string; options: PreviewOption[] }
/** Etsy kisisellestirme sorusu (buildPersonalization / taslak bicimi) */
interface PreviewQuestion {
  question_type?: string; question_text?: string; instructions?: string;
  required?: boolean; max_allowed_characters?: number; options?: Array<{ label: string }>;
}

export function EtsyListingPreview({ title, images, chartImages = [], price, variations, personalization, currency = "USD", pricesAreListPrices = false }: {
  title: string;
  images: string[];
  /** Chart gorselleri (zincir cetveli, font tablosu) — onizlemede ana gorselle birlikte */
  chartImages?: string[];
  /** Alicinin odeyecegi (indirimli) fiyat — varyasyon secilmeden gosterilen */
  price?: number | null;
  variations: PreviewVariation[];
  personalization?: { questions: unknown[] } | null;
  currency?: string;
  /** Fiyatlar Etsy LISTE fiyati mi (taslakta x2 kaydedilir) — indirimli fiyata cevrilir */
  pricesAreListPrices?: boolean;
}) {
  const toSale = (n: number) => (pricesAreListPrices ? n / LIST_PRICE_FACTOR : n);
  // Onizlemede yalnizca ana gorsel + chart'lar
  const first = abs(images[0]);
  const charts = chartImages.map(abs).filter((c) => c && c !== first);
  const [main, setMain] = useState(0);
  const [picked, setPicked] = useState<Record<number, string>>({});
  const vars = variations.map((v) => ({ ...v, options: v.options.filter((o) => o.included !== false) })).filter((v) => v.options.length);
  const questions = (personalization?.questions ?? []) as PreviewQuestion[];

  // Fiyat: secilen secenegin fiyati; secim yoksa en dusuk ("+" ile)
  const { shown, from } = useMemo(() => {
    for (const [i, label] of Object.entries(picked)) {
      const o = vars[Number(i)]?.options.find((x) => x.label === label);
      if (o?.sellingPrice != null) return { shown: toSale(o.sellingPrice), from: false };
    }
    const prices = vars.flatMap((v) => v.options.map((o) => o.sellingPrice)).filter((n): n is number => typeof n === "number" && n > 0);
    if (prices.length) return { shown: toSale(Math.min(...prices)), from: new Set(prices).size > 1 };
    return { shown: price != null ? (pricesAreListPrices ? price / LIST_PRICE_FACTOR : price) : null, from: false };
  }, [picked, vars, price, pricesAreListPrices]); // eslint-disable-line react-hooks/exhaustive-deps
  const money = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  const selectCls = "w-full rounded-full border border-[#c7c7c7] bg-white px-4 py-2.5 text-sm text-[#222]";

  return (
    <div className="grid gap-5 font-sans text-[#222] md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* Galeri */}
      <div>
        {first ? (
          <>
          <img src={[first, ...charts][Math.min(main, charts.length)]} alt="" className="aspect-square w-full rounded-xl border border-[#e1e3df] object-cover" />
          {/* Chart gorselleri (ana gorselin altinda; tiklayinca buyuk) */}
          {charts.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[first, ...charts].map((u, i) => (
                <button key={`${u}-${i}`} onClick={() => setMain(i)} title={i > 0 ? "Chart (fotoğrafların sonuna eklenir)" : "Ana görsel"}>
                  <img src={u} alt="" className={`h-12 w-12 rounded-md border-2 object-cover ${i === main ? "border-[#222]" : "border-[#e1e3df]"}`} />
                </button>
              ))}
            </div>
          )}
          </>
        ) : (
          <div className="flex aspect-square items-center justify-center rounded-xl border border-dashed border-[#c7c7c7] text-sm text-[#595959]">Fotoğraf yok</div>
        )}
      </div>

      {/* Satin alma kutusu */}
      <div>
        {shown != null && (
          <div className="mb-2 flex flex-wrap items-baseline gap-2">
            <span className="text-2xl font-bold text-[#222]">{money(shown)}{from ? "+" : ""}</span>
            <span className="text-sm text-[#595959] line-through">{money(shown * LIST_PRICE_FACTOR)}</span>
            <span className="text-sm font-semibold text-[#1e7d34]">{Math.round((1 - 1 / LIST_PRICE_FACTOR) * 100)}% off</span>
          </div>
        )}
        <p className="mb-4 text-lg leading-snug text-[#222]">{title}</p>
        {vars.map((v, i) => (
          <label key={`${v.name}-${i}`} className="mb-3 block">
            <span className="mb-1 block text-sm text-[#222]">{v.name}</span>
            <select className={selectCls} value={picked[i] ?? ""} onChange={(e) => setPicked({ ...picked, [i]: e.target.value })}>
              <option value="">Select an option</option>
              {v.options.map((o) => (
                <option key={o.label} value={o.label}>
                  {o.label}{o.sellingPrice != null && vars.length === 1 ? ` (${money(toSale(o.sellingPrice))})` : ""}
                </option>
              ))}
            </select>
          </label>
        ))}
        {questions.map((q, i) => (
          <div key={i} className="mb-3">
            <span className="block text-sm text-[#222]">{q.question_text ?? "Personalization"}{q.required === false ? " (optional)" : ""}</span>
            {q.question_type === "dropdown" ? (
              <select className={`${selectCls} mt-1`}>
                <option>Select an option</option>
                {(q.options ?? []).map((o) => <option key={o.label}>{o.label}</option>)}
              </select>
            ) : q.question_type === "unlabeled_upload" ? (
              <div className="mt-1 rounded-xl border-2 border-dashed border-[#c7c7c7] p-3 text-center text-[13px] text-[#595959]">Upload a file</div>
            ) : (
              <QuestionText q={q} />
            )}
          </div>
        ))}
        <div className="mt-2 rounded-full bg-[#222] py-3 text-center text-[15px] font-bold text-white">Add to cart</div>
      </div>
    </div>
  );
}

function QuestionText({ q }: { q: PreviewQuestion }) {
  const [v, setV] = useState("");
  const max = q.max_allowed_characters ?? 256;
  return (
    <>
      {q.instructions && <p className="mb-1 mt-0.5 whitespace-pre-line text-[13px] text-[#595959]">{q.instructions}</p>}
      <textarea rows={2} value={v} onChange={(e) => setV(e.target.value.slice(0, max))} className="w-full rounded-xl border border-[#c7c7c7] px-3 py-2 text-sm" />
      <p className="text-right text-xs text-[#595959]">{v.length}/{max}</p>
    </>
  );
}

/** Acilir-kapanir kutu icinde onizleme (sayfalarda yer kaplamasin) */
export function EtsyListingPreviewBox({ defaultOpen = false, ...props }: Parameters<typeof EtsyListingPreview>[0] & { defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-[#e1e3df] bg-[#f6f6f5]">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="text-[15px] font-bold text-[#222]">Etsy'de nasıl görünecek</span>
        <span className="text-sm text-[#595959] underline">{open ? "Gizle" : "Göster"}</span>
      </button>
      {open && <div className="rounded-b-xl border-t border-[#e1e3df] bg-white p-4"><EtsyListingPreview {...props} /></div>}
    </div>
  );
}
